// modules
import { get, has, isEmpty } from 'lodash'
import moment from 'moment'
import { createSelector } from 'reselect'

// utils
import { patientSelector } from 'src/store/modules/patient/selectors'

// constants
import { DATE_TIME_FORMAT } from 'src/constants'
import {
  convertFrequencyList,
  prepareMeasurementRuleList
} from 'src/utils/DataTrees'

export const measurementListSelector = createSelector(
  patientSelector,
  (profile = {}) =>
    profile.measurements.measurementList
      .map((item, key) => ({
        ...item,
        key,
        value: item.uuid,
        disabled: item.isAssigned || !item.isActive
      }))
      .sort((a, b) => +a.type - +b.type)
)

export const measurementList$ = createSelector(
  patientSelector,
  (profile = {}) =>
    profile.measurements.measurementList
      .map((item, key) => ({
        ...item,
        key,
        value: item.uuid,
        disabled: item.isAssigned || !item.isActive
      }))
      .sort((a, b) => +a.type - +b.type)
)

export const measurementValueSelector = createSelector(
  patientSelector,
  (profile) => {
    const measurementValue = get(
      profile,
      ['measurements', 'measurementValue'],
      {}
    )
    const rules = get(measurementValue, ['rules'], [])
    const frequency = get(measurementValue, ['frequency'], {})

    return {
      ...measurementValue,
      rules: prepareMeasurementRuleList(rules),
      frequency: convertFrequencyList(frequency)
    }
  }
)

export const measurementPatientListSelector = createSelector(
  patientSelector,
  (profile) =>
    profile.measurements.measurementPatientList.map((item, key) => ({
      ...item,
      key,
      rules: has(item, 'rules') ? prepareMeasurementRuleList(item.rules) : []
    }))
)

export const measurementDashboardList$ = createSelector(
  patientSelector,
  (profile) =>
    profile.measurements.measurementDashboardList.map((item, key) => ({
      ...item,
      key,
      rules: has(item, 'rules') ? prepareMeasurementRuleList(item.rules) : []
    }))
)

export const measurementPatientListAssignedSelector = createSelector(
  patientSelector,
  (profile) =>
    profile.measurements.measurementPatientListAssigned.map((item, key) => ({
      ...item,
      key,
      value: item.uuid,
      isActive: !!+item.isActive
    }))
)

export const measurementPatientValueSelector = createSelector(
  patientSelector,
  (profile) => {
    const measurementPatientValue = get(
      profile,
      ['measurements', 'measurementPatientValue'],
      {}
    )
    const rules = get(measurementPatientValue, ['rules'], [])
    const frequency = get(measurementPatientValue, ['frequency'], {})

    return {
      ...measurementPatientValue,
      rules: prepareMeasurementRuleList(rules),
      frequency: convertFrequencyList(frequency)
    }
  }
)

export const measurementPatientReadingListSelector = createSelector(
  patientSelector,
  (profile) => {
    const items = get(
      profile,
      ['measurements', 'measurementPatientReadingList'],
      []
    )

    const parseAlert = (alert) => {
      if (!alert?.uuid) return null

      let ruleId = alert.details?.ruleId ?? null
      try {
        const ruleValue = alert.details?.ruleValue
        const parsed = typeof ruleValue === 'string' ? JSON.parse(ruleValue) : ruleValue
        ruleId = ruleId ?? parsed?.ruleId ?? null
      } catch (_) {}

      return { ...alert, ruleId }
    }

    return items.map((item, key) => {
      const parsed = parseAlert(item.alert)

      return {
        ...item,
        key,
        alerts: parsed ? [parsed] : []
      }
    })
  }
)

export const measurementPatientReadingValueSelector = createSelector(
  patientSelector,
  (profile) => profile.measurements.measurementPatientReadingValue
)

export const measurementPatientReadingAnswerSelector = createSelector(
  patientSelector,
  (profile) => profile.measurements.measurementPatientReadingAnswer
)

export const measurementPredefinedFilter$ = createSelector(
  patientSelector,
  (profile) => profile.measurements.measurementPredefinedFilter
)

export const measurementActiveTab$ = createSelector(
  patientSelector,
  (profile) => profile.measurements.measurementActiveTab
)

export const measurementChartInfo$ = createSelector(
  patientSelector,
  (profile) => get(profile, ['measurements', 'measurementChartInfo'], {})
)

export const measurementChartInfoMapped$ = createSelector(
  measurementChartInfo$,
  (measurementChartInfo) => {
    if (isEmpty(measurementChartInfo)) return measurementChartInfo

    const missed = +get(measurementChartInfo, ['missed'], 0)
    const missedTotal = +get(measurementChartInfo, ['readings', 'length'], 0)
    const completed = get(measurementChartInfo, ['completed'], 0)

    return {
      insights: [
        ...get(measurementChartInfo, ['insights'], []),
        {
          count: missed,
          percent: missedTotal ? (missed / missedTotal) * 100 : missedTotal,
          title: 'Missed'
        }
      ].map((item, key, { length }) => [
        {
          ...item,
          percent: item.percent.toFixed(2),
          total: key !== length - 1 ? completed : missedTotal
        },
        {
          count:
            key !== length - 1
              ? completed - item.count
              : missedTotal - item.count,
          priority: null
        }
      ]),
      readings: get(measurementChartInfo, ['readings'], []).map((item) => ({
        ...item,
        frequencyDate: moment(item.frequencyDate).format(DATE_TIME_FORMAT)
      }))
    }
  }
)

export const loadingSync$ = createSelector(patientSelector, (profile) =>
  get(profile, ['measurements', 'loadingSync'], {})
)
